import {
  CreateDomainRequest,
  DomainSchema,
  NeedsDomainDto,
  UpdateDomainSchema,
} from '@act/shared/data-transfer-objects';
import { FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import useUnsavedChangesDialog from 'react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { useForm } from 'react-hook-form';
import {
  createNeedsMetadataSuccess,
  updateNeedsMetadataSuccess,
} from 'redux/slices/metaSlice';
import { needRequests } from 'redux/requests/need.requests';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { z } from 'zod';
import { useToast } from '../../../../app/ReusableToast';
import { AdminDrawerForm } from '../../AdminDrawer';

interface Props {
  open: boolean;
  setOpen: (value: boolean) => void;
  isEdit: boolean;
  selectedDomain?: NeedsDomainDto;
}

export const NeedsDomainCreateEdit = ({
  open,
  setOpen,
  isEdit,
  selectedDomain,
}: Props) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const schema = isEdit ? UpdateDomainSchema : DomainSchema;
  type DomainFormData = z.infer<typeof schema>;
  const { control, handleSubmit, formState } = useForm<DomainFormData>({
    defaultValues: selectedDomain || {
      label: '',
      key: '',
    },
    resolver: zodResolver(schema),
  });
  const [needDomainResult, createNeedDomainLoading, createNeedDomain] =
    useApiCall(needRequests.createNeedsMetadata);
  const [updateNeedDomainResult, updateNeedDomainLoading, updateNeedDomain] =
    useApiCall(needRequests.updateNeedsMetadata);
  const onSubmit = (data: DomainFormData) => {
    if (isEdit) {
      void updateNeedDomain({
        body: {
          ...data,
        },
        params: { type: 'domains', id: selectedDomain?.id },
      });
    } else {
      void createNeedDomain({
        body: {
          ...(data as CreateDomainRequest),
        },
        params: { type: 'domains' },
      });
    }
  };

  const formConfig: FormConfig<DomainFormData> = [
    {
      type: 'input',
      name: 'label',
      label: 'Activity Label',
      input: 'text',
    },
    {
      type: 'input',
      name: 'key',
      label: 'Key',
      input: 'text',
    },
  ];

  // Handle successful creation
  useEffect(() => {
    if (needDomainResult && !createNeedDomainLoading && open) {
      dispatch(
        createNeedsMetadataSuccess({
          data: needDomainResult,
          type: 'domains',
        })
      );
      toast('Domain created successfully');
      setOpen(false);
    }
  }, [needDomainResult, createNeedDomainLoading]);

  // Handle successful update
  useEffect(() => {
    if (updateNeedDomainResult && !updateNeedDomainLoading && open) {
      dispatch(
        updateNeedsMetadataSuccess({
          data: updateNeedDomainResult,
          type: 'domains',
        })
      );
      toast('Domain updated successfully');
      setOpen(false);
    }
  }, [updateNeedDomainResult, updateNeedDomainLoading]);

  const { isDirty } = formState;
  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };

  const DrawerContent = useMemo(() => {
    return (
      <FormBuilder
        config={formConfig}
        control={control}
        formState={formState}
      />
    );
  }, [control, formConfig, formState.errors]);

  return (
    <AdminDrawerForm
      title={isEdit ? `Edit Domain` : `Add New Domain`}
      children={DrawerContent}
      onSubmit={handleSubmit(onSubmit)}
      open={open}
      setOpen={handleClose}
    />
  );
};
